import React, { useState } from "react";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { FilterMatchMode, FilterOperator } from "primereact/api";
import { InputText } from "primereact/inputtext";
import { Calendar } from "primereact/calendar";
import { Dialog } from "primereact/dialog";
import { Tag } from "primereact/tag";
import { Dropdown } from "primereact/dropdown";
import { useDispatch, useSelector } from "react-redux";
import PrimaryButton from "../../shared/Button/PrimaryButton";
import { uploadImages } from "../../redux/actions/uploadFiles";
import { updateOrderStatus } from "../../redux/actions/shippingOrderAction";
import { hasPermission } from "../../utils/adminPermissions";
import { exportTableToCsv } from "../../utils/exportTableCsv";

export default function ShippingTable({
    heading,
    allShippingOrder,
    loading,
    history,
    search,
    setSearch,
    statusFilter,
    setStatusFilter,
    refreshData,
}) {
    const [selectedRow, setSelectedRow] = useState([]);
    const dispatch = useDispatch();
    const { userProfile } = useSelector((state) => state.profile);
    const canUpdateOrder = hasPermission(userProfile, "shipping_order.update");

    const [shipOrder, setShipOrder] = useState(null);
    const [receiptFile, setReceiptFile] = useState(null);
    const [shipping, setShipping] = useState(false);

    const openShipDialog = (order) => {
        setReceiptFile(null);
        setShipOrder(order);
    };

    const closeShipDialog = () => {
        setShipOrder(null);
        setReceiptFile(null);
        setShipping(false);
    };

    const handleReceiptChange = (e) => {
        const file = e.target.files?.[0];
        setReceiptFile(file || null);
    };

    const handleMarkAsShipped = async () => {
        if (!shipOrder) return;
        setShipping(true);
        try {
            let extraData = {};
            if (receiptFile) {
                const [path] = await uploadImages([receiptFile], dispatch);
                if (path) {
                    extraData.shippingReceipt = path;
                }
            }
            dispatch(
                updateOrderStatus(shipOrder._id, "DISPATCHED", extraData, () => {
                    closeShipDialog();
                    refreshData();
                })
            );
        } finally {
            setShipping(false);
        }
    };

    const actions = (d) => {
        return (
            <div className="flex justify-content-around">
                <i className="pi pi-eye cursor-pointer text-dark" onClick={() => history(`/shipping-order/${d._id}`)}></i>
                {canUpdateOrder && d.status === "PLACED" ? (
                    <i
                        className="pi pi-truck cursor-pointer text-dark ml-2"
                        title="Mark as Shipped"
                        onClick={() => openShipDialog(d)}
                    ></i>
                ) : null}
            </div>
        );
    };

    const filters = {
        time: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
        subscriptionExpireOn: { operator: FilterOperator.AND, constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] },
    };

    const formatDate = (date) => {
        return date?.toLocaleDateString?.("en-US", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
        });
    };

    const dateFilterTemplate = (options) => {
        return (
            <Calendar
                value={options.value}
                onChange={(e) => {
                    options.filterCallback(e.value, options.index);
                }}
                dateFormat="mm/dd/yy"
                placeholder="mm/dd/yyyy"
                mask="99/99/9999"
            />
        );
    };

    const orderSrNo = (r) => {
        const srNo = r.srNo ? r.srNo : r._id;
        return srNo;
    };

    const statusOptions = [
        { label: "Needs Dispatch", value: "PLACED" },
        { label: "Dispatched", value: "DISPATCHED" },
        { label: "Already Shipped", value: "ALREADY_SHIPPED" },
        { label: "All Orders", value: "ALL" },
    ];

    const exportAll = () => {
        exportTableToCsv("shipping-orders.csv", allShippingOrder, [
            { header: "Order ID", value: orderSrNo },
            { header: "Date", value: (row) => (row.time ? formatDate(row.time) : "-") },
            { header: "Name", value: (row) => [row?.user?.firstName, row?.user?.lastName].filter(Boolean).join(" ") || "-" },
            { header: "Vehicle Number", value: (row) => row?.vehicle?.vehicleNumber || "-" },
            { header: "Status", value: "status" },
        ]);
    };

    return (
        <>
            <div className="col-12">
                <div className="card">
                    <div className="flex justify-content-between mb-4 flex-wrap gap-2">
                        <h5>{heading}</h5>
                        <div className="flex align-items-center gap-2">
                            <Dropdown
                                value={statusFilter}
                                options={statusOptions}
                                onChange={(e) => setStatusFilter(e.value)}
                                placeholder="Filter Status"
                                className="w-12rem"
                            />
                            {setSearch ? (
                                <div className="p-input-icon-left">
                                    <i className="pi pi-search" />
                                    <InputText value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search" />
                                </div>
                            ) : null}
                            <PrimaryButton
                                label="Export CSV"
                                icon="pi pi-download"
                                onClick={exportAll}
                                disabled={!allShippingOrder.length}
                                severity="success"
                            />
                        </div>
                    </div>
                    <DataTable
                        paginator
                        value={allShippingOrder}
                        selection={selectedRow}
                        onSelectionChange={(e) => setSelectedRow(e.value)}
                        selectionMode="checkbox"
                        showGridlines
                        filter={filters}
                        filterDisplay="menu"
                        sortField="time"
                        sortOrder={-1}
                        rows={10}
                        rowsPerPageOptions={[10, 20,50, 100]}
                        loading={loading}
                        emptyMessage="No Order found."
                        rowClassName={(rowData) => (rowData.isNewRecord ? "p-highlight p-selectable-row" : "")}
                    >
                        <Column body={orderSrNo} header="Order ID "></Column>
                        <Column
                            field="time"
                            header="Date"
                            style={{ width: "150px" }}
                            dataType="date"
                            sortable
                            filterMatchMode="dateIs"
                            showFilterMatchModes={true}
                            body={(rowData) => formatDate(rowData.time)}
                            filter
                            filterElement={dateFilterTemplate}
                        ></Column>
                        {/* <Column field="user.userId" header="User Id" style={{ width: "250px" }}></Column> */}
                        <Column body={(rowData) => [rowData?.user?.firstName, rowData?.user?.lastName].filter(Boolean).join(" ") || "-"} header="Name"></Column>
                        <Column field="vehicle.vehicleNumber" header="Vehicle Number" style={{ width: "250px" }}></Column>
                        <Column
                            field="status"
                            header="Status"
                            body={(rowData) =>
                                rowData.status === "ALREADY_SHIPPED" ? (
                                    <Tag severity="warning" value="Already Shipped" title="A QR is already active on this vehicle — no shipment needed" />
                                ) : (
                                    rowData.status
                                )
                            }
                        ></Column>

                        <Column body={actions} style={{ width: "80px" }} header="Action"></Column>
                    </DataTable>
                    <Dialog
                        header="Mark as Shipped"
                        visible={!!shipOrder}
                        contentClassName="flex flex-column"
                        style={{ width: "35vw", minWidth: "350px" }}
                        onHide={closeShipDialog}
                    >
                        <p className="mb-3">
                            Order ID: <strong>{shipOrder?.srNo || shipOrder?._id}</strong>
                            {shipOrder?.user ? (
                                <>
                                    <br />
                                    Customer: {[shipOrder?.user?.firstName, shipOrder?.user?.lastName].filter(Boolean).join(" ") || "-"}
                                </>
                            ) : null}
                        </p>
                        <label htmlFor="shippingReceipt" className="mb-2">
                            Upload Shipping Receipt (optional)
                        </label>
                        <input
                            type="file"
                            id="shippingReceipt"
                            name="shippingReceipt"
                            accept="image/*,.pdf"
                            onChange={handleReceiptChange}
                        />
                        {receiptFile ? <small className="mt-2">Selected: {receiptFile.name}</small> : null}
                        <div className="flex justify-content-end gap-2 mt-4">
                            <PrimaryButton label="Cancel" className="p-button-outlined" onClick={closeShipDialog} />
                            {canUpdateOrder && <PrimaryButton label="Mark as Shipped" loading={shipping} onClick={handleMarkAsShipped} />}
                        </div>
                    </Dialog>
                </div>
            </div>
        </>
    );
}
